import React, { useState, useEffect } from 'react';
import { Routes, Route, useNavigate, Navigate } from 'react-router-dom';
import Timeline from './Timeline';
import Marketplace from './Marketplace';
import Meetings from './Meetings';
import CapTable from './components/CapTable';
import SettingsPage from './Settings';
import UsersAdmin from './UsersAdmin';
import Inbox from './Inbox';
import Admin from './Admin';

import HotelOnlineLogo from './components/HotelOnlineLogo';
import Login from './components/Login';
import ContactUserModal from './components/ContactUserModal';
import ContactSuccessPopup from './components/ContactSuccessPopup';
import { ContactProvider, useContactContext } from './contexts/ContactContext';
import { SettingsProvider } from './contexts/SettingsContext';
import { User } from './models/User';
import { UserApiService } from './services/userApi';
import { initializeSettings } from './config/settings';

import { getTrackingInfo } from './utils/trackingUtils';
import { auth, onAuthStateChanged, db } from './config/firebase.ts';
import { signOut, GoogleAuthProvider, signInWithPopup, signInWithEmailAndPassword } from 'firebase/auth';
import { initializeApp } from './utils/initializeApp';
import { collection, addDoc, query, where, getDocs, doc, setDoc } from 'firebase/firestore';
import { useAuth } from './hooks/useAuth';
import Layout from './components/Layout';
import Spinner from './components/Spinner';
import AdminCapTable from './components/admin/AdminCapTable';
import ManagementAccount from './components/ManagementAccount';
import { updateUser as updateUserInDb, getUserByFirebaseUid } from './data/userDatabase';
import InvitePage from './components/InvitePage';
import { requestNotificationPermission, onMessageListener } from './utils/messaging';
import { getAuth } from 'firebase/auth';
import Reports from './Reports';
import FinancialReports from './FinancialReports';

function AppContent() {
  const { user: currentUser, logout: handleLogout } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const { contactUser } = useContactContext();
  const navigate = useNavigate();

  useEffect(() => {
    if (currentUser) {
      onMessageListener()
        .then((payload) => {
          // Handle foreground message
          console.log('Received foreground message: ', payload);
          // Optionally show a toast or update UI
        })
        .catch((err) => console.log('failed: ', err));
    }
  }, [currentUser]);

  useEffect(() => {
    // Only fetch users if we have an authenticated user
    if (currentUser) {
      const fetchUsers = async () => {
        try {
          const usersRef = collection(db, 'users');
          const snapshot = await getDocs(usersRef);
          const usersList = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          })) as User[];
          setUsers(usersList);
        } catch (error) {
          console.error('Error fetching users:', error);
          // Don't show error to user, just log it
        }
      };

      fetchUsers();
    } else {
      // Clear users when logged out
      setUsers([]);
    }
  }, [currentUser]); // Only re-run when currentUser changes

  const handleAddUser = async (newUser: User) => {
    setUsers(prevUsers => [...prevUsers, newUser]);
  };

  const handleUpdateUser = async (updatedUser: User) => {
    try {
      await updateUserInDb(updatedUser);
      setUsers(prevUsers => prevUsers.map(user => 
        user.id === updatedUser.id ? updatedUser : user
      ));
    } catch (error) {
      console.error("Failed to update user:", error);
      // Optionally, show an error to the user here
    }
  };

  const handleRemoveUser = async (userId: string) => {
    setUsers(prevUsers => prevUsers.filter(user => user.id !== userId));
  };

  const handleContactSuccess = (recipientId: string) => {
    if (contactUser) {
      contactUser.triggerSuccessPopup(recipientId);
    }
  };

  const handleGoToInbox = () => {
    if (contactUser && contactUser.successRecipientId) {
      // Navigate to inbox with the recipient ID parameter to open the specific conversation
      navigate(`/inbox?userId=${contactUser.successRecipientId}`);
    } else {
      // Fallback to just the inbox
      navigate('/inbox');
    }
    if (contactUser) {
      contactUser.closeSuccessPopup();
    }
  };

  if (!currentUser) {
    // This can happen briefly on logout before the main App component redirects.
    // Return a spinner or null to avoid rendering the layout with a null user.
    return <Spinner />;
  }

  return (
    <>
      <Routes>
        <Route element={<Layout user={currentUser} onLogout={handleLogout} />}>
          <Route index element={<Timeline user={currentUser} users={users} onLogout={handleLogout} />} />
          <Route path="marketplace" element={<Marketplace user={currentUser} onLogout={handleLogout} />} />
          <Route path="meetings" element={<Meetings />} />
          
          <Route path="reports" element={<Reports />}>
            <Route path="management-accounts" element={<ManagementAccount />} />
            <Route path="financial-reports" element={<FinancialReports />} />
            <Route path="cap-table" element={<CapTable />} />
            <Route index element={<Navigate to="management-accounts" replace />} />
          </Route>

          <Route path="admin" element={<Admin user={currentUser} onLogout={handleLogout} />} />
          <Route path="settings" element={<SettingsPage user={currentUser} onLogout={handleLogout} onUpdateUser={handleUpdateUser} />} />
          <Route path="inbox" element={<Inbox user={currentUser} onLogout={handleLogout} />} />
          
        </Route>
      </Routes>

      {/* Global Contact User Modal */}
      {contactUser && (
        <ContactUserModal
          recipientId={contactUser.recipientId || ''}
          source={contactUser.source || 'Timeline'}
          isOpen={contactUser.isOpen}
          onClose={contactUser.closeContactModal}
          onSend={async (recipientId: string, content: string) => {
            try {
              await contactUser.sendMessage(recipientId, content, contactUser.source || 'Timeline');
            } catch (error) {
              console.error('Failed to send message:', error);
              throw error; // Re-throw so ContactUserModal can handle it
            }
          }}
          onSuccess={handleContactSuccess}
        />
      )}

      {/* Global Contact Success Popup */}
      {contactUser && contactUser.showSuccessPopup && contactUser.successRecipientId && (
        <ContactSuccessPopup
          isOpen={contactUser.showSuccessPopup}
          recipientName={users.find(u => u.id === contactUser.successRecipientId)?.fullName || contactUser.successRecipientId}
          onClose={contactUser.closeSuccessPopup}
          onGoToInbox={handleGoToInbox}
        />
      )}
    </>
  );
}

function App() {
  const { user, isLoading, logout } = useAuth();

  console.log('🚀 App component render - isLoading:', isLoading, 'user:', user ? user.email : 'null');

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, async (authUser) => {
      console.log('🔄 App onAuthStateChanged:', authUser ? authUser.email : 'null');
      if (authUser) {
        // User is signed in.
        console.log('Auth state confirmed, user is logged in.');
        // Don't request FCM permission here - let useAuth handle it when user is fully loaded
      } else {
        // User is signed out.
        console.log('Auth state confirmed, user is logged out.');
      }
    });

    // Cleanup subscription on unmount
    return () => unsubscribe();
  }, []); // Empty dependency array ensures this runs once on mount

  if (isLoading) {
    console.log('🔄 App showing loading spinner');
    return <Spinner />;
  }

  console.log('🔄 App rendering routes');
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/invite" element={<InvitePage />} />
      <Route
        path="/*"
        element={
          user ? (
            <SettingsProvider>
              <ContactProvider user={user}>
                <AppContent />
              </ContactProvider>
            </SettingsProvider>
          ) : (
            <Navigate to="/login" replace />
          )
        }
      />
    </Routes>
  );
}

export default App;